import React, { useCallback } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Group, Select, Stack, Text, TextInput } from '@mantine/core'

import { useTranscriptOrderChange } from './useTranscriptOrderChange'

import { FormErrors } from '@/components/Event/NewScript/components/common/forms/FormErrors'
import { TURNAROUND_TIME_OPTIONS, TurnaroundHours } from '@/constants'

type RowFieldErrors = {
  name?: { message?: string }
  email?: { message?: string }
}

type Props = {
  namePrefix: string
}

export const OrderConfirmationRow: React.FC<Props> = ({ namePrefix }) => {
  const { control, setValue, trigger, formState } = useFormContext()

  const transcriptOrder = useWatch({
    control,
    name: `${namePrefix}.transcript_order`,
  }) as string | null | undefined

  const name = useWatch({ control, name: `${namePrefix}.name` }) as
    | string
    | null
    | undefined
  const email = useWatch({ control, name: `${namePrefix}.email` }) as
    | string
    | null
    | undefined

  const setField = useCallback(
    (field: 'name' | 'email', value: string) => {
      setValue(`${namePrefix}.${field}`, value, {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [namePrefix, setValue],
  )

  const handleTranscriptOrderChange = useCallback(
    (newValue: string | null) => {
      setValue(`${namePrefix}.transcript_order`, newValue, {
        shouldDirty: true,
      })
      trigger([namePrefix, 'order_transcript', 'attendees'])
    },
    [namePrefix, setValue, trigger],
  )

  const onTurnaroundHoursChange = useTranscriptOrderChange(
    handleTranscriptOrderChange,
  )

  const { error } = control.getFieldState(namePrefix, formState) as {
    error?: RowFieldErrors
  }

  return (
    <Stack spacing={4}>
      <Group spacing="xs" align="center" position="left">
        <TextInput
          value={name ?? ''}
          onChange={event => setField('name', event.currentTarget.value)}
          aria-label="Attendee name"
          error={!!error?.name?.message}
          size="sm"
          sx={{ minWidth: 160 }}
        />
        <Text size="sm">within</Text>
        <Select
          value={transcriptOrder ?? TurnaroundHours.ROUGH_DRAFT.toString()}
          data={TURNAROUND_TIME_OPTIONS}
          onChange={value => {
            if (value !== null) {
              onTurnaroundHoursChange(value)
            }
          }}
          aria-label="Turnaround time"
          size="sm"
          sx={{ minWidth: 260 }}
          withinPortal
        />
        <Text size="sm">delivered to</Text>
        <TextInput
          value={email ?? ''}
          onChange={event => setField('email', event.currentTarget.value)}
          aria-label="Attendee email"
          error={!!error?.email?.message}
          size="sm"
          sx={{ minWidth: 200 }}
        />
        <Text size="sm">.</Text>
      </Group>
      {(error?.name?.message || error?.email?.message) && (
        <FormErrors errors={error?.name?.message ?? error?.email?.message} />
      )}
    </Stack>
  )
}
